import { useCallback } from 'react';
import { nanoid } from 'nanoid';
import { useReactFlow } from '@xyflow/react';
import { useNodeOperations } from '@/providers/node-operations';
import { useI18n } from '@/providers/i18n';
import { handleError } from '@/lib/error/handle';
import { autoGenerateAfterSave } from '@/lib/project-save';
import { DRAFT_FORMAL_QUALITY, DRAFT_TASK_TTL_MS, getFormalModelForDraft } from '@flowmuse/models/video/draft';

/** 正式节点上的样片来源标记：handle-generate 据此请求样片转正式，worker 成功后清除 */
export type DraftSourceData = {
    nodeId: string;
    videoUrl: string;
    /** 正式模型 ID；节点切换模型后不再按样片转正式 */
    modelId: string;
};

// 生成产物/进行中状态：正式节点从零开始，只沿用样片的输入与参数配置
const GENERATION_STATE_KEYS = [
    'generated', 'history', 'activeHistoryId', 'status', 'error', 'updatedAt',
    'processingGenerationId', 'processingTaskId', 'runtimeEstimatedTime', 'recovered',
    'content', 'sourceUrl', 'assetPreviewUrl', 'width', 'height', 'durationSeconds',
] as const;

/** 当前选中的样片视频生成时间；拿不到时返回 undefined（不做前端过期拦截，交给服务端判断） */
function getDraftCreatedAt(data: any): number | undefined {
    const history: any[] = Array.isArray(data?.history) ? data.history : [];
    const url = data?.generated?.url;
    const rec = history.find((h) => h?.id === data?.activeHistoryId) ?? history.find((h) => h?.url === url);
    const ts = Date.parse(rec?.createdAt ?? '');
    return Number.isFinite(ts) ? ts : undefined;
}

/** 节点是否为可转正式的样片：样片模型 + 已有生成视频；expired 表示超出 7 天有效期 */
export function getDraftConvertState(data: any): { convertible: boolean; expired: boolean } {
    const convertible = Boolean(getFormalModelForDraft(data?.model) && data?.generated?.url && data?.status !== 'processing');
    const createdAt = getDraftCreatedAt(data);
    const expired = createdAt !== undefined && Date.now() - createdAt > DRAFT_TASK_TTL_MS;
    return { convertible, expired };
}

/**
 * 一键样片转正式：在样片节点下方创建正式模型节点（复制入边与参数配置），随后自动生成。
 * 正式视频由模型复用样片的提示词/素材/时长/比例等，服务端按 draftSource 解析样片任务。
 */
export function useCreateFormalFromDraft(nodeId: string) {
    const { t } = useI18n();
    const { getNode, getEdges, addEdges, setNodes } = useReactFlow();
    const { addNode } = useNodeOperations();
    return useCallback(() => {
        try {
            const node = getNode(nodeId) as any;
            const data = (node?.data ?? {}) as Record<string, any>;
            const formalModelId = getFormalModelForDraft(data.model);
            const videoUrl = data.generated?.url as string | undefined;
            if (!node || !formalModelId || !videoUrl) return;

            const nextData: Record<string, any> = { ...data };
            for (const key of GENERATION_STATE_KEYS) delete nextData[key];
            const height = (node.measured?.height ?? 300) as number;
            const newId = addNode('video', {
                position: { x: node.position?.x ?? 0, y: (node.position?.y ?? 0) + height + 80 },
                selected: true,
                data: {
                    ...nextData,
                    viewMode: 'transform',
                    model: formalModelId,
                    quality: DRAFT_FORMAL_QUALITY,
                    draftSource: { nodeId, videoUrl, modelId: formalModelId } satisfies DraftSourceData,
                },
            });
            if (!newId) return;

            // 与样片共享上游输入，不连样片→正式的边（否则样片视频会被当作参考视频）
            const incomingEdges = getEdges().filter((e) => String(e.target) === String(nodeId) && String(e.source) !== String(nodeId));
            requestAnimationFrame(() => {
                if (incomingEdges.length > 0) {
                    addEdges(incomingEdges.map((e) => ({ ...e, id: nanoid(), target: newId, selected: false })));
                }
                try {
                    setNodes((prev: any[]) => prev.map((n: any) => ({ ...n, selected: String(n.id) === String(newId) })));
                } catch {}
                setTimeout(() => { void autoGenerateAfterSave(newId, 1500); }, 150);
            });
        } catch (error) {
            handleError(t('Request failed'), error);
        }
    }, [nodeId, getNode, getEdges, addEdges, setNodes, addNode, t]);
}
